import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

// Utils
import { removeSameValues } from 'common/utils/removeSameValues'

// Store
import { actions } from 'core/store'

// Constants
import { ROLE_MAPPING } from 'common/constants/formOptions'

// Hooks
import { useUpdateUser, useCreateUserActivationLink } from 'core/hooks/api'
import { useLocalStorage } from 'core/hooks/storage'

// Styled Elements
import {
  FormWrapper,
  TableButtonWrapper,
  TableRowWrapper,
  ButtonWrapper,
  Title,
  SubTitle,
  ColWrapper,
} from './UsersTableItem.elements'

// Views
import { UserDetailsForm } from 'views/layouts'
import { AccordionTableItem, Button } from 'views/components'

// Map Redux Props
const mapStateToProps = (state) => state
const mapDispatchToProps = actions

function UsersTableItem(props) {
  // Destructure
  const { actions, values } = props

  // Store Actions
  const { showAlert, toggleRemoveUserModal, setUserToDeact } = actions

  // Hooks
  const {
    updateUser,
    updateUserData,
    updateUserError,
    isUpdateUserLoading,
    isUpdateUserSuccess,
    isUpdateUserError
  } = useUpdateUser()
  const {
    createUserActivationLink,
    createUserActivationLinkError,
    isCreateUserActivationLinkLoading,
    isCreateUserActivationLinkSuccess
  } = useCreateUserActivationLink()

  // Local State
  const [initialData, setInitialData] = useState()
  const [viewMode, setViewMode] = useState(true)
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false)

  console.log(initialData)
  // Local storage
    const [userCredentials] = useLocalStorage('userCredentials', false)

  // Refs
  const formRef = useRef()

  // Variables
  const isLoading = isCreateUserActivationLinkLoading || isUpdateUserLoading

  // Functions
  const handleCancel = () => {
    setViewMode(true)
    if (formRef.current) {
      formRef.current.resetForm()
    }
  }

  const handleSave = (initial) => {
    const finalValues = removeSameValues(formRef?.current?.values, initial)
    delete finalValues.is_active
    delete finalValues.is_verified
    updateUser({ user_id: initialData?.user_id, ...finalValues })
  }

  const handleSuccess = () => {
    if (formRef.current) {
      formRef.current.resetForm({ values: updateUserData })
    }
    setInitialData(updateUserData)
    showAlert({ type: 'success', message: 'Successfully updated record' })
    setViewMode(true)
  }

  const handleFailure = () => {
    const errorMessage = updateUserError?.response?.data?.context || 'An error occurred in updating record'
    showAlert({ type: 'error', message: errorMessage })
  }

  const handleSendActivation = () => {
    createUserActivationLink({ user_id: initialData?.user_id })
  }

  const handleRemoveUser = () => {
    toggleRemoveUserModal()
    setUserToDeact({ user_id: initialData?.user_id, is_active: false })
  }

  // UseEffects
  useEffect(() => {
    setInitialData(values)
    if (formRef.current) {
      formRef.current.values = values
    }
  }, [values])

  useEffect(() => {
    if (isUpdateUserSuccess) handleSuccess()
  }, [isUpdateUserSuccess])

  useEffect(() => {
    if (isUpdateUserError) handleFailure()
  }, [isUpdateUserError])

  useEffect(() => {
    if (createUserActivationLinkError) {
      showAlert({ type: 'error', message: 'An error occurred in sending activation link' })
    }
  }, [createUserActivationLinkError])

  useEffect(() => {
    if (isCreateUserActivationLinkSuccess) {
      showAlert({ type: 'success', message: 'Successfully sent activation link' })
    }
  }, [isCreateUserActivationLinkSuccess])

  if (!initialData) return null

  return (
    <FormWrapper>
      <AccordionTableItem
        setIsExpanded={setIsDetailsExpanded}
        expandPanel={isDetailsExpanded}
        button={
          <TableButtonWrapper>
            <TableRowWrapper isExpanded={isDetailsExpanded}>
              <ColWrapper>
                <Title>
                  {`${initialData?.name_first || 'First Name'} ${initialData?.name_last || 'Last Name'}`}
                </Title>
                <SubTitle>Name of User</SubTitle>
              </ColWrapper>
              <ColWrapper>
                <Title>{ROLE_MAPPING?.[initialData?.role_id]?.label || 'User Role'}</Title>
                <SubTitle>User Role</SubTitle>
              </ColWrapper>
              <ColWrapper>
                <Title>{initialData?.is_verified ? 'Verified' : 'Unverified'}</Title>
                <SubTitle>Status</SubTitle>
              </ColWrapper>
            </TableRowWrapper>
          </TableButtonWrapper>
        }
        panel={
          <>
            <UserDetailsForm formRef={formRef} values={initialData} viewMode={viewMode} />
            <ButtonWrapper>
              {!initialData?.is_verified && (
                <Button onClick={handleSendActivation} disabled={isLoading}>
                  Send Activation
                </Button>
              )}

              {initialData?.is_active && userCredentials?.id !== initialData.user_id && (
                <Button onClick={handleRemoveUser} disabled={isLoading} color="secondary">
                  Remove User
                </Button>
              )}

              {viewMode ? (
                <Button color="primary" onClick={() => setViewMode(false)}>
                  Edit
                </Button>
              ) : (
                <>
                  <Button variant="outlined" onClick={handleCancel} disabled={isLoading}>
                    Cancel
                  </Button>
                  <Button onClick={() => handleSave(initialData)} disabled={isLoading}>
                    Save
                  </Button>
                </>
              )}
            </ButtonWrapper>
          </>
        }
      />
    </FormWrapper>
  )
}

// Default Props
UsersTableItem.defaultProps = {
  actions: {},
  values: {},
}

// Proptypes Validation
UsersTableItem.propTypes = {
  values: PropTypes.shape({
    id: PropTypes.string,
    name_first: PropTypes.string,
    name_last: PropTypes.string,
    role_id: PropTypes.number,
    is_verified: PropTypes.bool,
    is_active: PropTypes.bool,
  }),
  actions: PropTypes.shape({
    setUserToDeact: PropTypes.func,
    toggleRemoveUserModal: PropTypes.func,
    showAlert: PropTypes.func,
  }),
}

export default connect(mapStateToProps, mapDispatchToProps)(UsersTableItem)
